import React from 'react';
import { Card, Typography, Empty, Table, Button, Tag, Space, Spin, Alert } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { FileTextOutlined, ReloadOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { getMyDrafts } from '@/services/workboard.service';
import type { EnterpriseWorkItemProjection } from '@/types/enterprise-work-item';
import { useAuthStore } from '@/store/authStore';

const { Title, Paragraph, Text } = Typography;

const MyDraftsPage: React.FC = () => {
  const navigate = useNavigate();
  const userId = useAuthStore((s) => s.user?.id);
  const query = useQuery({
    queryKey: ['home-drafts', userId],
    queryFn: getMyDrafts,
    enabled: Boolean(userId),
    staleTime: 60_000,
  });

  const columns: ColumnsType<EnterpriseWorkItemProjection> = [
    {
      title: 'Document',
      key: 'doc',
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Text strong>{r.documentNumber || r.entityId}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {r.module} · {r.title}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 100,
      render: (s: string) => <Tag>{s}</Tag>,
    },
    {
      title: 'Updated',
      dataIndex: 'lastActivityAt',
      width: 130,
      render: (d?: string) => (d ? dayjs(d).format('DD MMM YYYY') : '—'),
    },
    {
      title: '',
      key: 'cta',
      width: 100,
      render: (_, r) => (
        <Button type="primary" size="small" onClick={() => navigate(r.actionRoute || '/dashboard')}>
          Open
        </Button>
      ),
    },
  ];

  if (query.isLoading && !query.data) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <Title level={2}>
        <FileTextOutlined /> My Drafts
      </Title>
      <Paragraph>
        Draft documents you own. Continue editing via Open — status changes stay on the domain document.
      </Paragraph>
      <Space style={{ marginBottom: 16 }}>
        <Link to="/dashboard">My Workboard</Link>
        <Button icon={<ReloadOutlined />} loading={query.isFetching} onClick={() => query.refetch()}>
          Refresh
        </Button>
      </Space>

      {query.error && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="Could not load drafts"
          description={(query.error as Error)?.message}
        />
      )}

      <Card>
        <Table
          rowKey="id"
          size="middle"
          loading={query.isFetching}
          columns={columns}
          dataSource={query.data?.items ?? []}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No drafts in your queue"
              />
            ),
          }}
          pagination={{ pageSize: 20 }}
        />
      </Card>
    </div>
  );
};

export default MyDraftsPage;
